/**
 * Segregation-of-duties (SoD) conflict detection for the user-assignment role
 * selector. Purely informational — the caller renders these as a **soft**,
 * non-blocking warning; nothing here prevents a save.
 *
 * Each role from the enriched `GET /api/v1/roles` catalog carries a
 * `conflicts` array of the *other* role codes it's typically kept separate
 * from (e.g. `security_admin.conflicts = ["finance_admin"]` and vice versa).
 * Given the set of currently-selected role codes, this returns the distinct
 * conflicting pairs among them, by display name, so the UI can render a
 * message like:
 *   "User & Security Administrator and Finance Administrator are typically
 *   kept separate (segregation of duties)."
 *
 * @param {string[]} selectedCodes - role codes currently selected/assigned
 * @param {Record<string, {name?: string, conflicts?: string[]}>} rolesById
 *   - role catalog keyed by `code`
 * @returns {{a: string, b: string}[]} distinct conflicting pairs (display names)
 */
export function computeRoleConflicts(selectedCodes = [], rolesById = {}) {
  const codes = Array.from(new Set(selectedCodes));
  const seenPairs = new Set();
  const pairs = [];

  for (const codeA of codes) {
    const roleA = rolesById[codeA];
    if (!roleA) continue;
    for (const codeB of roleA.conflicts || []) {
      if (codeB === codeA || !codes.includes(codeB)) continue;
      const pairKey = [codeA, codeB].sort().join("|");
      if (seenPairs.has(pairKey)) continue;
      seenPairs.add(pairKey);

      const roleB = rolesById[codeB];
      pairs.push({ a: roleA.name || codeA, b: roleB?.name || codeB });
    }
  }

  return pairs;
}
